import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createDepartment, getAllUsers } from '../../api/Index';
import './CreateDepartment.css';
import PageTitle from '../../components/UI/PageTitle';

const CreateDepartment = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    budget: '',
    description: '',
    head: '',
    members: []
  });

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch users for head and members selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getAllUsers();
        setUsers(response.data);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users for selection');
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleMemberChange = (userId) => {
    const updatedMembers = formData.members.includes(userId)
      ? formData.members.filter(id => id !== userId)
      : [...formData.members, userId];
    
    setFormData({
      ...formData,
      members: updatedMembers
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Validate that head is not in members
      if (formData.head && formData.members.includes(formData.head)) {
        setError('Department head cannot be in members list');
        setSubmitting(false);
        return;
      }

      const departmentData = {
        name: formData.name,
        description: formData.description,
        budget: parseFloat(formData.budget) || 0,
        head: formData.head || null,
        members: formData.members
      };
      
      await createDepartment(departmentData);
      navigate('/departments');
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to create department');
      console.error('Create department error:', err);
      setSubmitting(false);
    }
  };
  
  return (
    <div>
        <PageTitle title='Create Department' />
        <div className="create-department-container">
            <div className="form-container">
                {error && (
                    <div style={{
                        backgroundColor: '#ffe6e6',
                        border: '1px solid #ff0000',
                        borderRadius: '4px',
                        padding: '10px',
                        marginBottom: '20px',
                        color: '#cc0000'
                    }}>
                        {error}
                    </div>
                )}
                
                <form className="create-department-form" onSubmit={handleSubmit}>
                    
                    <div className="input-group">
                        <label className="input-label">Department Name *</label>
                        <input
                            type="text"
                            className="input"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter department name"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Budget</label>
                        <input
                            type="number"
                            className="input"
                            name="budget"
                            value={formData.budget}
                            onChange={handleInputChange}
                            placeholder="Enter department budget"
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Department Head</label>
                        <select
                            className="input"
                            name="head"
                            value={formData.head}
                            onChange={handleInputChange}
                            disabled={loadingUsers}
                        >
                            <option value="">Select Department Head (Optional)</option>
                            {users.map(user => (
                                <option key={user._id} value={user._id}>
                                    {user.name} ({user.email})
                                </option>
                            ))}
                        </select>
                        {loadingUsers && (
                            <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                                Loading users...
                            </small>
                        )}
                    </div>

                    <div className="input-group">
                        <label className="input-label">Department Members</label>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '4px', padding: '10px' }}>
                            {loadingUsers ? (
                                <p>Loading users...</p>
                            ) : users.length === 0 ? (
                                <p>No users available</p>
                            ) : (
                                users.map(user => (
                                    <div key={user._id} style={{ marginBottom: '8px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={formData.members.includes(user._id)}
                                                onChange={() => handleMemberChange(user._id)}
                                                disabled={formData.head === user._id}
                                                style={{ marginRight: '8px' }}
                                            />
                                            <span style={{ 
                                                color: formData.head === user._id ? '#999' : '#333',
                                                textDecoration: formData.head === user._id ? 'line-through' : 'none'
                                            }}>
                                                {user.name} ({user.email})
                                                {formData.head === user._id && ' - Department Head'}
                                            </span>
                                        </label>
                                    </div>
                                ))
                            )}
                        </div>
                        <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                            Select multiple members. Department head cannot be a member.
                        </small>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Department Description *</label>
                        <textarea
                            className="text-area"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter department description"
                            required
                        />
                    </div>

                    <button type="submit" className='form-button' disabled={submitting}>
                        {submitting ? 'Creating...' : 'Create Department'}
                    </button>
                </form>
            </div>
        </div>
    </div>
  );
};

export default CreateDepartment;